import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  ViewModule as CardViewIcon,
  ViewList as ListViewIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
  School as CertificateIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { studentAPI, classAPI, certificateMasterAPI, studentCertificateAPI } from '../services/api';

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('list'); // 'card' 또는 'list'
  const [sortConfig, setSortConfig] = useState({ key: 'is_active', direction: 'desc' });
  
  // 자격증 관련 상태
  const [openCertificateDialog, setOpenCertificateDialog] = useState(false);
  const [editingStudentCert, setEditingStudentCert] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentCertificates, setStudentCertificates] = useState([]);
  const [certificateMasters, setCertificateMasters] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    phone: '',
    parent_phone: '',
    memo: '',
    is_active: true,
    class_ids: [],
  });

  const [certificateFormData, setCertificateFormData] = useState({
    certificate_id: '',
    acquired_date: '',
    score: '',
  });

  // 학생 목록 조회
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getStudents();
      setStudents(response.data.students);
    } catch (error) {
      console.error('학생 목록 조회 실패:', error);
      showSnackbar('학생 목록 조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 수업 목록 조회
  const fetchClasses = async () => {
    try {
      const response = await classAPI.getClasses();
      setClasses(response.data.classes);
    } catch (error) {
      console.error('수업 목록 조회 실패:', error);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, []);

  // 폼 초기화
  const resetForm = () => {
    setFormData({
      name: '',
      birth_date: '',
      phone: '',
      parent_phone: '',
      memo: '',
      is_active: true,
      class_ids: [],
    });
    setEditingStudent(null);
  };

  // 다이얼로그 열기
  const handleOpenDialog = (student = null) => {
    if (student) {
      setEditingStudent(student);
      setFormData({
        name: student.name,
        birth_date: student.birth_date,
        phone: student.phone || '',
        parent_phone: student.parent_phone || '',
        memo: student.memo || '',
        is_active: student.is_active,
        class_ids: student.classes ? student.classes.map(c => c.id) : [],
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  // 폼 데이터 변경
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 수업 선택 변경
  const handleClassChange = (classId) => {
    setFormData(prev => ({
      ...prev,
      class_ids: (prev.class_ids || []).includes(classId)
        ? (prev.class_ids || []).filter(id => id !== classId)
        : [...(prev.class_ids || []), classId],
    }));
  };

  // 학생 저장
  const handleSave = async () => {
    try {
      if (editingStudent) {
        await studentAPI.updateStudent(editingStudent.id, formData);
        showSnackbar('학생 정보가 수정되었습니다.', 'success');
      } else {
        await studentAPI.createStudent(formData);
        showSnackbar('학생이 등록되었습니다.', 'success');
      }
      
      fetchStudents();
      handleCloseDialog();
    } catch (error) {
      console.error('학생 저장 실패:', error);
      showSnackbar('학생 저장에 실패했습니다.', 'error');
    }
  };

  // 학생 삭제
  const handleDelete = async (studentId) => {
    if (window.confirm('정말로 이 학생을 삭제하시겠습니까?')) {
      try {
        await studentAPI.deleteStudent(studentId);
        showSnackbar('학생이 삭제되었습니다.', 'success');
        fetchStudents();
      } catch (error) {
        console.error('학생 삭제 실패:', error);
        showSnackbar('학생 삭제에 실패했습니다.', 'error');
      }
    }
  };

  // 활성 상태 변경
  const handleStatusChange = async (studentId, isActive) => {
    try {
      await studentAPI.updateStudentStatus(studentId, isActive);
      showSnackbar(`학생 상태가 ${isActive ? '활성' : '비활성'}으로 변경되었습니다.`, 'success');
      fetchStudents();
    } catch (error) {
      console.error('상태 변경 실패:', error);
      showSnackbar('상태 변경에 실패했습니다.', 'error');
    }
  };

  // 스낵바 표시
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 스낵바 닫기
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/\D/g, '');
    
    // 길이에 따라 포맷팅
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    } else {
      // 11자리 초과시 11자리까지만
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // 전화번호 입력 핸들러
  const handlePhoneChange = (field, value) => {
    const formattedValue = formatPhoneNumber(value);
    handleInputChange(field, formattedValue);
  };

  // 엑셀 내보내기
  const handleExportToExcel = () => {
    try {
      // 엑셀 데이터 준비
      const excelData = students.map(student => {
        const studentClasses = student.classes || [];
        
        return {
          '학생명': student.name,
          '생년월일': student.birth_date || '-',
          '전화번호': student.phone || '-',
          '보호자 전화번호': student.parent_phone || '-',
          '수강 수업': studentClasses.map(cls => cls.name).join(', ') || '-',
          '상태': student.is_active ? '활성' : '비활성',
          '메모': student.memo || '-',
          '등록일': student.created_at ? new Date(student.created_at).toLocaleDateString('ko-KR') : '-',
          '수정일': student.updated_at ? new Date(student.updated_at).toLocaleDateString('ko-KR') : '-'
        };
      });

      // 워크시트 생성
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // 컬럼 너비 설정
      const columnWidths = [
        { wch: 15 }, // 학생명
        { wch: 12 }, // 생년월일
        { wch: 15 }, // 전화번호
        { wch: 15 }, // 보호자 전화번호
        { wch: 20 }, // 수강 수업
        { wch: 8 },  // 상태
        { wch: 30 }, // 메모
        { wch: 12 }, // 등록일
        { wch: 12 }  // 수정일
      ];
      worksheet['!cols'] = columnWidths;

      // 워크북 생성
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '수강생 목록');

      // 파일명 생성 (현재 날짜 포함)
      const now = new Date();
      const dateString = now.toISOString().split('T')[0];
      const fileName = `수강생_목록_${dateString}.xlsx`;

      // 파일 다운로드
      XLSX.writeFile(workbook, fileName);
      
      showSnackbar('엑셀 파일이 성공적으로 다운로드되었습니다.', 'success');
    } catch (error) {
      console.error('엑셀 내보내기 실패:', error);
      showSnackbar('엑셀 내보내기에 실패했습니다.', 'error');
    }
  };

  // 자격증 마스터 목록 조회
  const fetchCertificateMasters = async () => {
    try {
      const response = await certificateMasterAPI.getCertificateMasters(true); // 활성화된 것만
      setCertificateMasters(response.data.certificate_masters);
    } catch (error) {
      console.error('자격증 마스터 목록 조회 실패:', error);
      showSnackbar('자격증 마스터 목록 조회에 실패했습니다.', 'error');
    }
  };

  // 학생별 자격증 취득 목록 조회
  const fetchStudentCertificates = async (studentId) => {
    try {
      const response = await studentCertificateAPI.getStudentCertificatesByStudent(studentId);
      setStudentCertificates(response.data.student_certificates);
    } catch (error) {
      console.error('학생별 자격증 취득 목록 조회 실패:', error);
      showSnackbar('학생별 자격증 취득 목록 조회에 실패했습니다.', 'error');
    }
  };

  // 자격증 다이얼로그 열기
  const handleOpenCertificateDialog = (student, studentCert = null) => {
    setSelectedStudent(student);
    setEditingStudentCert(studentCert);
    
    if (studentCert) {
      setCertificateFormData({
        certificate_id: studentCert.certificate_id,
        acquired_date: studentCert.acquired_date,
        score: studentCert.score || '',
      });
    } else {
      setCertificateFormData({
        certificate_id: '',
        acquired_date: '',
        score: '',
      });
    }
    
    setOpenCertificateDialog(true);
    fetchStudentCertificates(student.id);
    fetchCertificateMasters();
  };

  // 자격증 다이얼로그 닫기
  const handleCloseCertificateDialog = () => {
    setOpenCertificateDialog(false);
    setSelectedStudent(null);
    setEditingStudentCert(null);
    setCertificateFormData({
      certificate_id: '',
      acquired_date: '',
      score: '',
    });
  };

  // 자격증 폼 데이터 변경
  const handleCertificateInputChange = (field, value) => {
    setCertificateFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 자격증 저장
  const handleCertificateSave = async () => {
    try {
      // 필수 필드 검증
      if (!certificateFormData.certificate_id) {
        showSnackbar('자격증을 선택해주세요.', 'error');
        return;
      }
      
      if (!certificateFormData.acquired_date) {
        showSnackbar('취득일을 입력해주세요.', 'error');
        return;
      }
      
      const certificateData = {
        student_id: selectedStudent.id,
        certificate_id: certificateFormData.certificate_id,
        acquired_date: certificateFormData.acquired_date,
        score: certificateFormData.score || null,
      };
      
      if (editingStudentCert) {
        // 기존 자격증 취득 수정
        await studentCertificateAPI.updateStudentCertificate(editingStudentCert.id, certificateData);
        showSnackbar('자격증 취득 정보가 수정되었습니다.', 'success');
      } else {
        // 새 자격증 취득 등록
        await studentCertificateAPI.createStudentCertificate(certificateData);
        showSnackbar('자격증 취득이 등록되었습니다.', 'success');
      }
      
      fetchStudentCertificates(selectedStudent.id);
      handleCloseCertificateDialog();
    } catch (error) {
      console.error('자격증 저장 실패:', error);
      showSnackbar('자격증 저장에 실패했습니다.', 'error');
    }
  };

  // 자격증 삭제
  const handleCertificateDelete = async (studentCertId) => {
    if (window.confirm('정말로 이 자격증 취득 기록을 삭제하시겠습니까?')) {
      try {
        await studentCertificateAPI.deleteStudentCertificate(studentCertId);
        showSnackbar('자격증 취득 기록이 삭제되었습니다.', 'success');
        fetchStudentCertificates(selectedStudent.id);
      } catch (error) {
        console.error('자격증 삭제 실패:', error);
        showSnackbar('자격증 삭제에 실패했습니다.', 'error');
      }
    }
  };

  // 정렬 처리
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 정렬된 학생 목록
  const sortedStudents = React.useMemo(() => {
    if (!students || students.length === 0) {
      return [];
    }
    
    return [...students].sort((a, b) => {
      // 기본적으로 활성 상태 우선 정렬
      if (sortConfig.key !== 'is_active') {
        if (a.is_active !== b.is_active) {
          return b.is_active - a.is_active; // 활성 상태가 먼저
        }
      }
      
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // 날짜 정렬
      if (sortConfig.key === 'birth_date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // 문자열 정렬
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [students, sortConfig]);

  // 정렬 아이콘 렌더링
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <UnfoldMoreIcon />;
    }
    return sortConfig.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          수강생 관리
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={handleExportToExcel}
            title="엑셀 내보내기"
            sx={{ mr: 1 }}
          >
            <FileDownloadIcon />
          </IconButton>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="card">
              <CardViewIcon />
            </ToggleButton>
            <ToggleButton value="list">
              <ListViewIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            학생 등록
          </Button>
        </Box>
      </Box>

      {viewMode === 'card' ? (
        <Grid container spacing={3}>
          {sortedStudents.map((student) => (
            <Grid item xs={12} sm={6} md={4} key={student.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {student.name}
                    </Typography>
                    <Chip
                      label={student.is_active ? '활성' : '비활성'}
                      color={student.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    생년월일: {student.birth_date}
                  </Typography>
                  {student.phone && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      전화번호: {student.phone}
                    </Typography>
                  )}
                  {student.parent_phone && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      학부모 전화번호: {student.parent_phone}
                    </Typography>
                  )}
                  {student.memo && (
                    <Typography variant="body2" color="text.secondary">
                      메모: {student.memo}
                    </Typography>
                  )}
                  {student.classes && student.classes.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        수강 수업:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {student.classes.map((cls) => (
                          <Chip
                            key={cls.id}
                            label={cls.name}
                            size="small"
                            variant="outlined"
                            sx={{
                              backgroundColor: cls.color || '#90caf9',
                              color: '#333',
                              borderColor: cls.color || '#90caf9',
                              '&:hover': {
                                backgroundColor: cls.color || '#90caf9',
                                opacity: 0.8
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(student)}
                  >
                    수정
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CertificateIcon />}
                    onClick={() => handleOpenCertificateDialog(student)}
                    color="primary"
                  >
                    자격증
                  </Button>
                  <IconButton
                    size="small"
                    color={student.is_active ? 'warning' : 'success'}
                    onClick={() => handleStatusChange(student.id, !student.is_active)}
                    title={student.is_active ? '비활성화' : '활성화'}
                  >
                    {student.is_active ? <InactiveIcon /> : <ActiveIcon />}
                  </IconButton>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(student.id)}
                  >
                    삭제
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                    이름
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {getSortIcon('name')}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('birth_date')}>
                    생년월일
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {getSortIcon('birth_date')}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('parent_phone')}>
                    학부모 전화번호
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {getSortIcon('parent_phone')}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('classes')}>
                    수강 수업
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {getSortIcon('classes')}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('memo')}>
                    메모
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {getSortIcon('memo')}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStudents.map((student) => (
                <TableRow 
                  key={student.id}
                  sx={{ 
                    backgroundColor: !student.is_active ? 'grey.100' : 'inherit',
                    opacity: !student.is_active ? 0.7 : 1
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      {student.name}
                    </Box>
                  </TableCell>
                  <TableCell>{student.birth_date}</TableCell>
                  <TableCell>{student.parent_phone || '-'}</TableCell>
                  <TableCell>
                    {student.classes && student.classes.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {student.classes.map((cls) => (
                          <Chip
                            key={cls.id}
                            label={cls.name}
                            size="small"
                            variant="outlined"
                            sx={{
                              backgroundColor: cls.color || '#90caf9',
                              color: '#333',
                              borderColor: cls.color || '#90caf9',
                              '&:hover': {
                                backgroundColor: cls.color || '#90caf9',
                                opacity: 0.8
                              }
                            }}
                          />
                        ))}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{student.memo || '-'}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(student)}
                        title="수정"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenCertificateDialog(student)}
                        title="자격증 관리"
                        color="primary"
                      >
                        <CertificateIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color={student.is_active ? 'warning' : 'success'}
                        onClick={() => handleStatusChange(student.id, !student.is_active)}
                        title={student.is_active ? '비활성화' : '활성화'}
                      >
                        {student.is_active ? <InactiveIcon /> : <ActiveIcon />}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(student.id)}
                        title="삭제"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 학생 등록/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStudent ? '학생 정보 수정' : '학생 등록'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="이름"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="생년월일"
                type="date"
                value={formData.birth_date}
                onChange={(e) => handleInputChange('birth_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="전화번호"
                value={formData.phone}
                onChange={(e) => handlePhoneChange('phone', e.target.value)}
                placeholder="010-1234-5678"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="학부모 전화번호"
                value={formData.parent_phone}
                onChange={(e) => handlePhoneChange('parent_phone', e.target.value)}
                placeholder="010-1234-5678"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="메모"
                multiline
                rows={3}
                value={formData.memo}
                onChange={(e) => handleInputChange('memo', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="활성 상태"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                수강할 수업 선택
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {classes && classes.length > 0 ? classes.map((cls) => (
                  <Chip
                    key={cls.id}
                    label={cls.name}
                    color={formData.class_ids && formData.class_ids.includes(cls.id) ? 'primary' : 'default'}
                    onClick={() => handleClassChange(cls.id)}
                    clickable
                    sx={{
                      backgroundColor: formData.class_ids && formData.class_ids.includes(cls.id) 
                        ? (cls.color || '#90caf9') 
                        : 'inherit',
                      color: formData.class_ids && formData.class_ids.includes(cls.id) 
                        ? 'white' 
                        : 'inherit',
                      borderColor: cls.color || '#90caf9',
                      '&:hover': {
                        backgroundColor: formData.class_ids && formData.class_ids.includes(cls.id) 
                          ? (cls.color || '#90caf9') 
                          : (cls.color || '#90caf9'),
                        opacity: formData.class_ids && formData.class_ids.includes(cls.id) ? 0.8 : 0.3
                      }
                    }}
                  />
                )) : (
                  <Typography variant="body2" color="text.secondary">
                    등록된 수업이 없습니다.
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSave} variant="contained">
            {editingStudent ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 자격증 관리 다이얼로그 */}
      <Dialog open={openCertificateDialog} onClose={handleCloseCertificateDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedStudent && `${selectedStudent.name}님의 자격증 관리`}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">자격증 목록</Typography>
            </Box>
            
            {studentCertificates.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>자격증명</TableCell>
                      <TableCell>발급기관</TableCell>
                      <TableCell>취득일</TableCell>
                      <TableCell>점수</TableCell>
                      <TableCell align="center">작업</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentCertificates.map((studentCert) => (
                      <TableRow key={studentCert.id}>
                        <TableCell>{studentCert.certificate_name}</TableCell>
                        <TableCell>{studentCert.issuing_organization || '-'}</TableCell>
                        <TableCell>{studentCert.acquired_date || '-'}</TableCell>
                        <TableCell>{studentCert.score || '-'}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCertificateDialog(selectedStudent, studentCert)}
                              title="수정"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCertificateDelete(studentCert.id)}
                              title="삭제"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                취득한 자격증이 없습니다.
              </Typography>
            )}
          </Box>

          {/* 자격증 취득 등록/수정 폼 */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {editingStudentCert ? '자격증 취득 수정' : '자격증 취득 등록'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="자격증"
                  value={certificateFormData.certificate_id}
                  onChange={(e) => handleCertificateInputChange('certificate_id', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value="">자격증을 선택하세요</option>
                  {certificateMasters.map((certificate) => (
                    <option key={certificate.id} value={certificate.id}>
                      {certificate.name} ({certificate.issuing_organization})
                    </option>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="취득일"
                  type="date"
                  value={certificateFormData.acquired_date}
                  onChange={(e) => handleCertificateInputChange('acquired_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="점수"
                  value={certificateFormData.score}
                  onChange={(e) => handleCertificateInputChange('score', e.target.value)}
                  placeholder="점수를 입력하세요 (선택사항)"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseCertificateDialog}>닫기</Button>
          <Button onClick={handleCertificateSave} variant="contained">
            {editingStudentCert ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentManagement;
